import React, { useState } from 'react';
import { ArrowRight, Lock, Mail, Loader2 } from 'lucide-react';
import { useAuth } from '../context';
import { Input } from '@/components/ui/input';

interface LoginProps {
  onLoginSuccess?: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      onLoginSuccess?.();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cmd-canvas min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-sm animate-fade-in">
        {/* Dark green header panel with logo */}
        <div
          className="rounded-t p-6 flex flex-col items-center gap-3"
          style={{ background: '#073331', border: '1px solid rgba(196,195,183,0.16)', borderBottom: 'none' }}
        >
          <div className="bg-white rounded p-2 w-44">
            <img src="/optimal_long.png" alt="Optimal Trappstädning" className="w-full h-auto" />
          </div>
          <span className="cmd-label" style={{ color: '#8FA89B' }}>
            CLEANMANAGER — INLOGGNING
          </span>
        </div>

        {/* White cmd-panel form with reticle ticks */}
        <div className="cmd-panel cmd-panel-inner rounded-b p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label htmlFor="email" className="cmd-label">
                EMAIL
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#607C6E]" size={16} />
                <Input
                  id="email"
                  type="email"
                  className="pl-9 font-mono text-sm focus-visible:ring-[#8A6A47] focus-visible:border-[#8A6A47]"
                  placeholder="namn@optimaltrappstadning.se"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="cmd-label">
                LÖSENORD
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#607C6E]" size={16} />
                <Input
                  id="password"
                  type="password"
                  className="pl-9 font-mono text-sm focus-visible:ring-[#8A6A47] focus-visible:border-[#8A6A47]"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
            </div>

            {error && (
              <div
                className="text-xs font-mono p-3 rounded"
                style={{
                  background: 'rgba(194,65,56,0.10)',
                  color: '#C24138',
                  border: '1px solid rgba(194,65,56,0.25)',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded flex items-center justify-center gap-2 cmd-label text-[11px] text-white transition-colors disabled:opacity-60"
              style={{ background: '#073331', letterSpacing: '0.12em' }}
              onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#0B423E')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#073331')}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  LADDAR…
                </>
              ) : (
                <>
                  LOGGA IN
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <p className="cmd-label text-center text-[9px] pt-1">
              KONTAKTA ADMINISTRATÖR FÖR ÅTKOMST
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};
